/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TicketsIndex.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Grid,
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import CommonBreadcrumbs from "../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import ProjectVariantDialog from "../../../components/customComponentsV2/vlc/ProjectVarientAccordian/ProjectVariantDialog";
import ProjectvariantSearchbar from "../../../components/customComponentsV2/common/projectSearchbar/ProjectvariantSearchbar";
import TicketsTable from "../intersections/TicketsTable";
import { TICKETS_TYPES } from "../../../config/appConstants";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import TicketsFilter from "./TicketsFilter";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";

function TicketsIndex() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const tabFromUrl = Number.parseInt(searchParams.get("tab"), 10) || 0;
  const ticketTypes = useMemo(
    () => [
      TICKETS_TYPES.VEHICLE_SYSTEM,
      TICKETS_TYPES.VEHICLE_COMPONENT,
      TICKETS_TYPES.EE,
      TICKETS_TYPES.SWM,
    ],
    [],
  );
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const initialTicketType = ticketTypes[tabFromUrl] || TICKETS_TYPES.VEHICLE_SYSTEM;
  const [selectedTicketType, setSelectedTicketType] = useState(initialTicketType);

  const [ticketDetails, setTicketDetails] = useState([]);
  const [selectedIntersectionIds, setSelectedIntersectionIds] = useState([]);
  const [page] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterData, setFilterData] = useState({ archive_status: "ACTIVE" });

  const memoizedProjectVariant = useMemo(
    () => ({
      projectId: selectedProject?.project_id,
      variantId: selectedVariant?.variant_id,
    }),
    [selectedProject?.project_id, selectedVariant?.variant_id],
  );

  const fetchTickets = useCallback(
    async (requestObj = {}) => {
      if (!selectedTicketType) return;

      try {
        const resp = await dispatch(
          HandleApiActions({
            ...typeApprovalAPI?.get_tickets,
            params: {
              project_id: memoizedProjectVariant.projectId,
              variant_id: memoizedProjectVariant.variantId,
              page_size: rowsPerPage,
              page_number: page,
              status: "100,150,200,300,400,500,600,999",
              tickets_type: selectedTicketType,
              archive_status: "ACTIVE",
              ...filterData,
              ...requestObj,
            },
            show_toast: false,
          }),
        );

        if (resp?.error || !resp?.data) {
          setTicketDetails([]);
          setTotalCount(0);
          return;
        }

        setTicketDetails(resp.data);
        setTotalCount(resp?.total_count || 0);
      } catch (err) {
        console.error("fetchTickets error", err);
        setTicketDetails([]);
        setTotalCount(0);
      }
    },
    [
      dispatch,
      memoizedProjectVariant.projectId,
      memoizedProjectVariant.variantId,
      rowsPerPage,
      page,
      selectedTicketType,
      filterData,
    ],
  );

  useEffect(() => {
    if (memoizedProjectVariant.projectId && memoizedProjectVariant.variantId) {
      fetchTickets();
    }
  }, [memoizedProjectVariant, fetchTickets]);

  const handleTabChange = (tabIndex) => {
    setSelectedTicketType(ticketTypes[tabIndex]);
    setTicketDetails([]);
  };
  return (
    <Grid container>
      <Grid container>
        <Grid item sm={12} sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[{ label: "Regulatory Compliance" }, { label: "Tickets" }]}
          />
        </Grid>
      </Grid>

      <Grid container sx={{ height: "100%", alignItems: "center", p: 2 }}>
        <Grid item sm={12}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box>
              <TicketsFilter
                pageName={"tickets"}
                getFilterDetails={(data) => {
                  setFilterData(data);
                }}
              />
            </Box>

            <Box>
              <ProjectvariantSearchbar />
            </Box>

            <ProjectVariantDialog />
          </Box>
        </Grid>
        <CustomTabsV2
          onTabChange={handleTabChange}
          centerTabs={true}
          defaultTab={tabFromUrl || 0}
          tabs={[
            {
              name: "Vehicle System",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.VEHICLE_SYSTEM}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableAction={true}
                  showPagination={true}
                  filterData={filterData}
                  parentPage="tickets"
                />
              ),
            },
            {
              name: "Vehicle Component",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.VEHICLE_COMPONENT}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableAction={true}
                  showPagination={true}
                  filterData={filterData}
                  parentPage="tickets"
                />
              ),
            },
            {
              name: "EE Component",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.EE}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableAction={true}
                  showPagination={true}
                  filterData={filterData}
                  parentPage="tickets"
                />
              ),
            },
            {
              name: "Software Module",
              count: "",
              content: (
                <TicketsTable
                  tickets_type={TICKETS_TYPES.SWM}
                  ticketDetails={ticketDetails}
                  selectedTicketType={selectedTicketType}
                  setSelectedTicketType={setSelectedTicketType}
                  selectedIntersectionIds={selectedIntersectionIds}
                  setSelectedIntersectionIds={setSelectedIntersectionIds}
                  totalCount={totalCount}
                  fetchTickets={fetchTickets}
                  enableAction={true}
                  showPagination={true}
                  filterData={filterData}
                  parentPage="tickets"
                />
              ),
            },
          ]}
        />
      </Grid>
    </Grid>
  );
}

export default TicketsIndex;
